// Empty string = same-origin (staging/production nginx proxy). Dev: .env.development
const API_BASE_URL = import.meta.env.VITE_API_URL ?? ''

/** Encode a single path segment for use in a URL path (spaces, unicode, etc.). */
function encodePathSegment(segment: string): string {
  return encodeURIComponent(segment);
}

export interface UploadFileResponse {
  libraryObjectId: number;
  message: string;
}

export interface LibraryObjectUploadResponse {
  id: number;
  movieId: number;
  name: string;
  description?: string;
  type: string;
  fileName: string;
  url?: string;
  thumbnailUrl?: string;
  fullPath?: string;
  width: number;
  height: number;
}

export interface VectorPackageSummary {
  id: string;
  name: string;
  fileCount: number;
}

export interface VectorPackageFile {
  id: string;
  packageId: string;
  name: string;
  fileName: string;
  width: number;
  height: number;
  url: string;
}

/**
 * Uploads a file to the server in chunks.
 * On the final chunk, returns the library object (including signed URLs).
 */
export async function uploadFile(
  file: File,
  movieId: number,
  onProgress?: (progress: number) => void,
  accessToken?: string | null
): Promise<LibraryObjectUploadResponse> {
  const CHUNK_SIZE = 1024 * 1024; // 1MB chunks
  const chunks = Math.ceil(file.size / CHUNK_SIZE);

  for (let chunk = 0; chunk < chunks; chunk++) {
    const start = chunk * CHUNK_SIZE;
    const end = Math.min(start + CHUNK_SIZE, file.size);
    const blob = file.slice(start, end);

    const formData = new FormData();
    formData.append('file', blob);

    const url = `${API_BASE_URL}/api/Movies/upload?chunk=${chunk}&chunks=${chunks}&name=${encodeURIComponent(file.name)}&movieid=${movieId}`;

    const headers: HeadersInit = {};
    if (accessToken) {
      headers['Authorization'] = `Bearer ${accessToken}`;
    }

    const response = await fetch(url, {
      method: 'POST',
      body: formData,
      headers,
      credentials: 'include',
    });

    if (!response.ok) {
      throw new Error(`Upload failed: ${response.statusText}`);
    }

    if (onProgress) {
      onProgress(((chunk + 1) / chunks) * 100);
    }

    if (chunk === chunks - 1) {
      const contentType = response.headers.get('content-type') ?? '';
      if (contentType.toLowerCase().includes('application/json')) {
        return (await response.json()) as LibraryObjectUploadResponse;
      }

      // Legacy plain-text library object id fallback
      const result = await response.text();
      const libraryObjectId = parseInt(result, 10);
      if (!Number.isFinite(libraryObjectId)) {
        throw new Error('Upload failed: No library object returned');
      }
      return {
        id: libraryObjectId,
        movieId,
        name: file.name.replace(/\.[^/.]+$/, ''),
        type: 'image',
        fileName: file.name,
        width: 0,
        height: 0,
      };
    }
  }

  throw new Error('Upload failed: No library object returned');
}

/**
 * @deprecated Direct /Users paths are no longer used. Prefer server-provided `url` fields.
 */
export function getAssetUrl(userId: string, movieId: number, fileName: string): string {
  void userId;
  return `${API_BASE_URL}/api/assets/${movieId}/${encodePathSegment(fileName)}?variant=original`;
}

/**
 * @deprecated Prefer server-provided `thumbnailUrl`.
 */
export function getThumbnailUrl(userId: string, movieId: number, fileName: string): string {
  void userId;
  return `${API_BASE_URL}/api/assets/${movieId}/${encodePathSegment(fileName)}?variant=thumb`;
}

/**
 * @deprecated Prefer server-provided preview URLs when available.
 */
export function getPreviewUrl(userId: string, movieId: number, fileName: string): string {
  void userId;
  return `${API_BASE_URL}/api/assets/${movieId}/${encodePathSegment(fileName)}?variant=preview`;
}

/**
 * Resolves asset references for rendering.
 * Prefers already-signed `/api/assets/...` URLs; does not invent signatures client-side.
 */
export function resolveAssetUrl(pathOrFileName: string, _userId?: string, _movieId?: number): string {
  if (!pathOrFileName) return pathOrFileName;

  if (/^(https?:)?\/\//i.test(pathOrFileName) || pathOrFileName.startsWith('data:')) {
    return pathOrFileName;
  }

  // Already a signed (or API) asset path
  if (
    pathOrFileName.startsWith('/api/assets/') ||
    pathOrFileName.startsWith('/api/vector-packages/') ||
    pathOrFileName.includes('/api/assets/')
  ) {
    if (pathOrFileName.startsWith('http')) return pathOrFileName;
    return `${API_BASE_URL}${pathOrFileName.startsWith('/') ? '' : '/'}${pathOrFileName}`;
  }

  // Legacy Users/... paths are no longer valid for <img>; leave empty so UI can fall back.
  if (pathOrFileName.startsWith('Users/') || pathOrFileName.startsWith('/Users/')) {
    console.warn('[assets] Ignoring legacy Users/ path; use signed url from API', pathOrFileName);
    return '';
  }

  return pathOrFileName;
}

function authHeaders(accessToken?: string | null): Record<string, string> {
  return accessToken ? { Authorization: `Bearer ${accessToken}` } : {};
}

export async function fetchVectorPackages(accessToken?: string | null): Promise<VectorPackageSummary[]> {
  const response = await fetch(`${API_BASE_URL}/api/vector-packages`, {
    headers: authHeaders(accessToken),
  });
  if (!response.ok) throw new Error('Failed to load vector packages');
  return (await response.json()) as VectorPackageSummary[];
}

export async function fetchVectorPackageFiles(
  packageId: string,
  accessToken?: string | null
): Promise<VectorPackageFile[]> {
  const response = await fetch(`${API_BASE_URL}/api/vector-packages/${encodeURIComponent(packageId)}/files`, {
    headers: authHeaders(accessToken),
  });
  if (!response.ok) throw new Error('Failed to load vector package files');
  return (await response.json()) as VectorPackageFile[];
}

export async function uploadVectorPackage(
  name: string,
  file: File,
  accessToken?: string | null
): Promise<VectorPackageSummary> {
  const body = new FormData();
  body.append('name', name);
  body.append('file', file);
  const response = await fetch(`${API_BASE_URL}/api/vector-packages`, {
    method: 'POST',
    headers: authHeaders(accessToken),
    body,
  });
  if (!response.ok) {
    const data = (await response.json().catch(() => ({}))) as { error?: string };
    throw new Error(data.error || 'Failed to upload vector package');
  }
  return (await response.json()) as VectorPackageSummary;
}

export async function importGitLibrary(
  body: { url: string; branch?: string; path?: string; format?: 'svg' | 'png' },
  accessToken?: string | null,
): Promise<VectorPackageSummary[]> {
  const response = await fetch(`${API_BASE_URL}/api/vector-packages/git`, {
    method: 'POST',
    headers: { ...authHeaders(accessToken), 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!response.ok) {
    const payload = (await response.json().catch(() => null)) as { error?: string } | null;
    throw new Error(payload?.error || 'Import failed');
  }
  return (await response.json()) as VectorPackageSummary[];
}

export async function deleteVectorPackage(packageId: string, accessToken?: string | null): Promise<void> {
  const response = await fetch(`${API_BASE_URL}/api/vector-packages/${encodeURIComponent(packageId)}`, {
    method: 'DELETE',
    headers: authHeaders(accessToken),
  });
  if (!response.ok && response.status !== 204) {
    const data = (await response.json().catch(() => ({}))) as { error?: string };
    throw new Error(data.error || 'Failed to delete vector package');
  }
}

export async function importVectorPackageFile(
  packageId: string,
  objectKey: string,
  movieId: number,
  accessToken?: string | null
): Promise<LibraryObjectUploadResponse> {
  const response = await fetch(
    `${API_BASE_URL}/api/vector-packages/${encodeURIComponent(packageId)}/import`,
    {
      method: 'POST',
      headers: {
        ...authHeaders(accessToken),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ movieId, key: objectKey }),
    }
  );
  if (!response.ok) {
    const data = (await response.json().catch(() => ({}))) as { error?: string };
    throw new Error(data.error || 'Failed to import vector');
  }
  return (await response.json()) as LibraryObjectUploadResponse;
}
